import React, { useState } from 'react';
import Modal from './Modal';
import { BuyTicketModalProps } from '../../types/modal';
import { ticketApi } from '../../services/ticketApi';
import { useNotification } from '../../context/NotificationContext';
import { InlineLoading } from '../ui/LoadingSpinner';

const TICKET_BUNDLE = 100;
const BUNDLE_PRICE = 250; // 100 fiş = 250 TL

const BuyTicketModal: React.FC<BuyTicketModalProps> = ({ isOpen, onClose, onBuy, userId }) => {
  const [quantity, setQuantity] = useState(TICKET_BUNDLE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess } = useNotification();

  const handleDecrease = () => setQuantity(q => Math.max(TICKET_BUNDLE, q - TICKET_BUNDLE));
  const handleIncrease = () => setQuantity(q => q + TICKET_BUNDLE);
  const total = (quantity / TICKET_BUNDLE) * BUNDLE_PRICE;

  const handleBuy = async () => {
    if (!userId) {
      setError('Kullanıcı bilgisi bulunamadı');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await ticketApi.purchaseTickets(userId, {
        quantity,
        payment_method: 'cash',
      });

      if (result.success) {
        showSuccess('Fiş Satın Alındı', `${quantity} fiş başarıyla satın alındı!`);
        if (onBuy) {
          onBuy(quantity, result.new_balance);
        }
        onClose();
      } else {
        setError('Fiş satın alma işlemi başarısız');
      }
    } catch (err) {
      console.error('Fiş satın alma hatası:', err);
      setError(err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Fiş Satın Al">
      <div className="flex flex-col items-center gap-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg w-full">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full shadow-sm px-3 py-1">
          <button
            onClick={handleDecrease}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-green-500 text-white text-base font-bold transition hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400"
            disabled={quantity === TICKET_BUNDLE || loading}
            aria-label="Azalt"
          >
            –
          </button>
          <span className="w-16 text-center text-lg font-semibold select-none">{quantity} Fiş</span>
          <button
            onClick={handleIncrease}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-green-500 text-white text-base font-bold transition hover:bg-green-600"
            aria-label="Arttır"
            disabled={loading}
          >
            +
          </button>
        </div>

        <div className="text-lg font-medium text-gray-700">
          Toplam: <span className="font-bold text-green-700">{total} TL</span>
        </div>

        <div className="flex gap-3 w-full mt-2">
          <button
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50"
            onClick={onClose}
            disabled={loading}
          >
            İptal
          </button>
          <button
            className="flex-1 bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50"
            onClick={handleBuy}
            disabled={loading}
          >
            {loading ? <InlineLoading text="İşleniyor..." /> : 'Satın Al'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BuyTicketModal;
